import { useEffect, useState } from "react";
import { Alert } from "@/ui";
import { clearSessionExpired, sessionExpired } from "@/lib/api";
import { useLanguage } from "@/i18n/language";

/**
 * Explains why someone is looking at a login screen they did not ask for.
 *
 * A session that lapses mid-task drops the person here with no context, which
 * reads as the app having thrown them out at random — the same complaint the
 * old behaviour produced, just moved to a different screen. Saying "it ran
 * out, sign in again" costs one line and turns a fault into a fact.
 *
 * The flag is read once on mount and cleared immediately, so it explains this
 * arrival and not the next visit to the same page.
 */
export function SessionExpiredNotice({ bilingual = false }: { bilingual?: boolean }) {
  const [show] = useState(sessionExpired);
  // `bilingual` marks the karigar sign-in: that copy follows the chosen
  // language (Hindi until they pick). Staff copy is English.
  const { t } = useLanguage(bilingual ? "hi" : "en");

  useEffect(() => {
    clearSessionExpired();
  }, []);

  if (!show) return null;

  return (
    <Alert kind="info" title={bilingual ? t("समय समाप्त", "Session expired") : "Session expired"}>
      {bilingual
        ? t(
            "सुरक्षा के लिए आप अपने आप लॉगआउट हो गए। दोबारा लॉगिन करें — आपके अंक सुरक्षित हैं।",
            "You were signed out automatically for your safety. Sign in again — your points are safe.",
          )
        : "You were signed out automatically. Please sign in again to continue."}
    </Alert>
  );
}
